import colors from '@lmc/common/constants'
import type { GoalStatus, User } from '@lmc/ts-common'
import { Text } from '@lmc/ui-kit/src/general'
import dayjs from 'dayjs'
import _ from 'lodash'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { NAMESPACE } from '../../../../../locale'
import { goalReportsConstants } from '../../../../../locale/goalReports'
import {
  getTagColorByStatus,
  TagUpperCase
} from '../../../../Residents/screens/ResidentsGoalsScreen/components/GoalsList/GoalItem'
import useGoalDueDate from '../hooks/useGoalDueDate'
import GoalInTimeIcon from '../icons/GoalInTimeIcon'
import GoalOverdueIcon from '../icons/GoalOverdueIcon'
import type { GoalSummary } from '../types'
import GoalProgress from './GoalProgress'

const GoalReportsRow = ({
  goal,
  onClick,
  lightVariant = false,
  testId
}: {
  goal: GoalSummary
  onClick?: (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => void
  lightVariant?: boolean
  testId?: string
}) => {
  const carers = useSelector(
    (state: { data: { carers: User[] } }) => state.data.carers
  )

  const carer = _.find(carers, { id: goal?.reviewedBy })
  const { t } = useTranslation(NAMESPACE.GOAL_REPORTS)

  const { formattedDueDate, icon, textColor } = useGoalDueDate(
    goal.dueDate,
    goal.status
  )
  const getName = (item: any) =>
    item ? `${item.name.first} ${item.name.last}` : ''

  const reviewDate = dayjs(goal.lastReviewedDate).format('DD/MM/YY · HH:mm')
  const reviewedBy = getName(carer)
  const subtitle = `Reviewed ${reviewDate} · ${reviewedBy}`

  const statusTagColor = getTagColorByStatus(goal?.status as GoalStatus)

  return (
    <Container
      onClick={onClick}
      lightVariant={lightVariant}
      data-testid={testId ? `${testId}-goal-report-row` : 'goal-report-row'}
    >
      <GoalProgress
        percentage={goal.progress}
        status={goal.status}
        data-testid={
          testId
            ? `${testId}-goal-report-row-progress`
            : 'goal-report-row-progress'
        }
      />
      <ContentContainer
        data-testid={
          testId
            ? `${testId}-goal-report-row-content`
            : 'goal-report-row-content'
        }
      >
        <GoalTitle level={1} weight="bold">
          {goal.title}
        </GoalTitle>
        <GoalSubtitle level={2} weight="medium">
          {subtitle}
        </GoalSubtitle>
      </ContentContainer>
      <StatusContainer
        data-testid={
          testId ? `${testId}-goal-report-row-status` : 'goal-report-row-status'
        }
      >
        {formattedDueDate && (
          <DueDateContainer
            data-testid={
              testId
                ? `${testId}-goal-report-row-due-date`
                : 'goal-report-row-due-date'
            }
          >
            <DueDate color={textColor} level={1}>
              {formattedDueDate}
            </DueDate>
            {icon === 'overdue' ? (
              <GoalOverdueIcon />
            ) : icon === 'inTime' ? (
              <GoalInTimeIcon />
            ) : null}
          </DueDateContainer>
        )}

        <TagUpperCase color={statusTagColor}>
          {t(goalReportsConstants[goal.status as GoalStatus])}
        </TagUpperCase>
      </StatusContainer>
    </Container>
  )
}

export default GoalReportsRow

const Container = styled.div<{ lightVariant: boolean }>`
  background-color: ${({ theme, lightVariant }) =>
    lightVariant ? theme.color.neutral.color11 : theme.color.neutral.color10};
  display: flex;
  border-radius: 16px;
  padding: 16px 24px;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  cursor: pointer;
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
`

const GoalTitle = styled(Text)`
  color: ${colors.black};
`

const GoalSubtitle = styled(Text)`
  color: ${(props) => props.theme.color?.neutral.color4};
`

const StatusContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`

const DueDate = styled(Text)<{ color: string }>`
  color: ${(p) => p.color};
`

const DueDateContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`
